import React, { useEffect, useState } from "react";
import { Grid, Box, Typography } from "@mui/material";
import { styled } from '@mui/material/styles';
import { DirectionsCar, EmojiPeople, Speed, AccountBalanceWallet } from '@mui/icons-material';
import CountUp from 'react-countup';
import axios from "../../axiosInstance";

// Styled components to match the main dashboard cards
const StatsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, #f6f9fc 0%, #f1f4f8 100%)',
  borderRadius: 24,
}));

const StatCard = styled('div')(({ gradient }) => ({
  position: 'relative',
  padding: '20px',
  minHeight: '180px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  background: gradient,
  borderRadius: 20,
  overflow: 'hidden',
  color: '#fff',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
    '& .stat-icon': {
      transform: 'scale(1.05) rotate(8deg)',
    },
  },
}));

const IconContainer = styled('div')({
  position: 'absolute',
  right: '-20px',
  top: '-20px',
  width: '120px',
  height: '120px',
  borderRadius: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.15,
  transition: 'transform 0.3s ease-in-out',
});

const StatValue = styled(Typography)({
  fontSize: '2.5rem',
  fontWeight: 800,
  color: 'white',
  textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
  zIndex: 1,
});

const StatLabel = styled(Typography)({
  fontSize: '1.1rem',
  color: 'rgba(255,255,255,0.9)',
  fontWeight: 600,
  zIndex: 1,
});

const StatSubtitle = styled(Typography)({
  color: 'rgba(255,255,255,0.85)',
  fontSize: '0.875rem',
});

export const DashboardAmbulanceComapny = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [metrics, setMetrics] = useState({
    drivers: 0,
    vehicles: 0,
    trips: 0,
    revenue: 0,
    credits: 0,
    pocketMoney: 0,
  });
  const [recentTrips, setRecentTrips] = useState([]);

  // Helper function to check if a date is in the current month
  const isCurrentMonth = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();
    return date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();
  };

  useEffect(() => {
    let mounted = true;
    async function fetchData() {
      try {
        setLoading(true);
        setError("");
        const [driversRes, vehiclesRes, transactionsRes, creditsRes] = await Promise.all([
          axios.get("/drivers"),
          axios.get("/vehicles"),
          axios.get("/transactions"),
          axios.get("/credits"),
        ]);
        if (!mounted) return;

        const driversArr = Array.isArray(driversRes.data) ? driversRes.data : [];
        const tripsArr = Array.isArray(transactionsRes.data) ? transactionsRes.data : [];
        const creditsArr = Array.isArray(creditsRes.data) ? creditsRes.data : [];

        // Filter data for current month only
        const currentMonthTrips = tripsArr.filter(t => isCurrentMonth(t.createdAt));
        const currentMonthCredits = creditsArr.filter(c => isCurrentMonth(c.createdAt));

        // Calculate monthly totals
        const totalRevenue = currentMonthTrips.reduce((sum, t) => sum + (parseFloat(t.revenue) || 0), 0);
        const totalCredits = currentMonthCredits.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
        const totalPocketMoney = driversArr.reduce((sum, d) => sum + (parseFloat(d.pocket) || 0), 0);

        setMetrics({
          drivers: driversArr.length,
          vehicles: Array.isArray(vehiclesRes.data) ? vehiclesRes.data.length : 0,
          trips: currentMonthTrips.length, // Only current month trips
          revenue: totalRevenue, // Only current month revenue
          credits: totalCredits, // Only current month credits
          pocketMoney: totalPocketMoney,
        });

        // Show recent trips from current month
        setRecentTrips(currentMonthTrips.slice(0, 5).map(t => ({
          id: t.id,
          date: t.createdAt,
          driver: t.driver?.firstName ? `${t.driver.firstName} ${t.driver.lastName || ''}`.trim() : t.driverId,
          amount: t.actualPrice,
          revenue: t.revenue,
        })));
      } catch (e) {
        console.error(e);
        if (mounted) setError("Failed to load dashboard data.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchData();
    return () => { mounted = false; };
  }, []);

  return (
    <StatsContainer>
      <Box mb={2}>
        <Typography variant="h5" fontWeight={700} color="text.primary">
          Ambulance Company Dashboard - {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          All statistics reset monthly (showing current month data only)
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard gradient={'linear-gradient(135deg, #6B46C1 0%, #9F7AEA 100%)'}>
            <IconContainer className="stat-icon">
              <EmojiPeople sx={{ fontSize: 80, color: 'white' }} />
            </IconContainer>
            <Box>
              <StatLabel>Drivers</StatLabel>
              <StatValue>
                <CountUp end={metrics.drivers} duration={2} separator="," />
              </StatValue>
              <StatSubtitle>Company scoped</StatSubtitle>
            </Box>
            <Box sx={{ position: 'absolute', bottom: 10, right: 10, opacity: 0.15 }}>
              <Speed sx={{ fontSize: 40, color: 'white' }} />
            </Box>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard gradient={'linear-gradient(135deg, #F6AD55 0%, #ED8936 100%)'}>
            <IconContainer className="stat-icon">
              <DirectionsCar sx={{ fontSize: 80, color: 'white' }} />
            </IconContainer>
            <Box>
              <StatLabel>Vehicles</StatLabel>
              <StatValue>
                <CountUp end={metrics.vehicles} duration={2} separator="," />
              </StatValue>
              <StatSubtitle>Company scoped</StatSubtitle>
            </Box>
            <Box sx={{ position: 'absolute', bottom: 10, right: 10, opacity: 0.15 }}>
              <Speed sx={{ fontSize: 40, color: 'white' }} />
            </Box>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard gradient={'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)'}>
            <IconContainer className="stat-icon">
              <Speed sx={{ fontSize: 80, color: 'white' }} />
            </IconContainer>
            <Box>
              <StatLabel>Trips</StatLabel>
              <StatValue>
                <CountUp end={metrics.trips} duration={2} separator="," />
              </StatValue>
              <StatSubtitle>This month only</StatSubtitle>
            </Box>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard gradient={'linear-gradient(135deg, #4834D4 0%, #686DE0 100%)'}>
            <IconContainer className="stat-icon">
              <Speed sx={{ fontSize: 80, color: 'white' }} />
            </IconContainer>
            <Box>
              <StatLabel>Revenue (ETB)</StatLabel>
              <StatValue>
                <CountUp end={metrics.revenue} duration={2} separator="," decimals={2} />
              </StatValue>
              <StatSubtitle>This month only</StatSubtitle>
            </Box>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard gradient={'linear-gradient(135deg, #00B894 0%, #00CEC9 100%)'}>
            <IconContainer className="stat-icon">
              <AccountBalanceWallet sx={{ fontSize: 80, color: 'white' }} />
            </IconContainer>
            <Box>
              <StatLabel>Credits (ETB)</StatLabel>
              <StatValue>
                <CountUp end={metrics.credits} duration={2} separator="," decimals={2} />
              </StatValue>
              <StatSubtitle>Monthly credits</StatSubtitle>
            </Box>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard gradient={'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)'}>
            <IconContainer className="stat-icon">
              <AccountBalanceWallet sx={{ fontSize: 80, color: 'white' }} />
            </IconContainer>
            <Box>
              <StatLabel>Total Pocket Money (ETB)</StatLabel>
              <StatValue>
                <CountUp end={metrics.pocketMoney} duration={2} separator="," decimals={2} />
              </StatValue>
              <StatSubtitle>All drivers combined</StatSubtitle>
            </Box>
          </StatCard>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Recent Trips</Typography>
        <Box component="div" sx={{ background: '#fff', borderRadius: 2, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f7f7f9' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Driver</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Price</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {recentTrips.map((t) => (
                <tr key={t.id}>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{t.id}</td>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{new Date(t.date).toLocaleString()}</td>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{t.driver}</td>
                  <td style={{ padding: '8px', textAlign: 'right', borderTop: '1px solid #eee' }}>{t.amount}</td>
                  <td style={{ padding: '8px', textAlign: 'right', borderTop: '1px solid #eee' }}>{t.revenue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>{error}</Typography>
      )}
    </StatsContainer>
  );
};
